import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from "@angular/core"
import { provideRouter } from "@angular/router"
import { BrowserModule } from "@angular/platform-browser"
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from "@angular/common/http"
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async"
import {
	IPublicClientApplication,
	PublicClientApplication,
	InteractionType,
	BrowserCacheLocation,
	LogLevel
} from "@azure/msal-browser"
import {
	MsalInterceptor,
	MSAL_INSTANCE,
	MsalInterceptorConfiguration,
	MsalGuardConfiguration,
	MSAL_GUARD_CONFIG,
	MSAL_INTERCEPTOR_CONFIG,
	MsalService,
	MsalGuard,
	MsalBroadcastService
} from "@azure/msal-angular"
import { appRoutes } from "./app.routes"
import { providePrimeNG } from "primeng/config"
import Aura from "@primeng/themes/aura"
import { EnvService } from "./services/env.service"

export function loggerCallback(logLevel: LogLevel, message: string) {
	const envService = new EnvService()

	if (envService.appConfig.logger.msalLogs) {
		console.log(
			`%cMSAL-LOGS %c${LogLevel[logLevel]}`,
			"color: #2a919a; font-weight: 900; line-height: 1.2",
			"font-weight: 900; line-height: 1.2; text-decoration: underline; text-decoration-style: double;",
			message
		)
	}
}

export function MSALInstanceFactory(): IPublicClientApplication {
	const envService = new EnvService()

	return new PublicClientApplication({
		auth: {
			clientId: envService.appConfig.msalConfig.auth.clientId,
			authority: envService.appConfig.msalConfig.auth.authority,
			redirectUri: envService.appConfig.msalConfig.auth.redirectUri,
			postLogoutRedirectUri: envService.appConfig.msalConfig.auth.postLogoutRedirectUri
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage
		},
		system: {
			allowPlatformBroker: false, // Disables WAM Broker
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Verbose,
				piiLoggingEnabled: false
			}
		}
	})
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const envService = new EnvService()

	const protectedResourceMap = new Map<string, string[]>()

	protectedResourceMap.set(envService.appConfig.domain, [
		envService.appConfig.msalConfig.auth.clientScopeProtocol +
			envService.appConfig.msalConfig.auth.clientId +
			envService.appConfig.msalConfig.auth.clientScopeEndpoint
	])

	protectedResourceMap.set(
		envService.appConfig.msalConfig.apiConfig.uri,
		envService.appConfig.msalConfig.apiConfig.scopes
	)

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap
	}
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	const envService = new EnvService()

	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: [...envService.appConfig.msalConfig.apiConfig.scopes]
		},
		loginFailedRoute: envService.appConfig.msalConfig.auth.loginFailedRoute
	}
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(appRoutes),
		importProvidersFrom(BrowserModule),
		provideAnimationsAsync(),
		providePrimeNG({
			theme: {
				preset: Aura,
				options: {
					prefix: "p",
					darkModeSelector: true,
					cssLayer: true /* {
						name: "primeng",
						order: "theme, base, primeng"
					}*/
				}
			},
			ripple: false,
			zIndex: {
				modal: 1100, // dialog, sidebar
				overlay: 1000, // dropdown, overlay panel
				menu: 1000, // overlay menus
				tooltip: 1100 // tooltip
			}
		}),
		provideHttpClient(withInterceptorsFromDi()),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService
	]
}
